import { Fragment, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Select from 'components/UnifiedSelect';

import Div from 'components/Div';
import FormInput from 'components/FormInput/index';
import PageTitle from 'components/PageTitle/index';
import { getComplaintFilterOptions, getComplaintIntelligence } from '../api';
import { loadSharedComplaintFilters, saveSharedComplaintFilters } from '../filters/sharedFilters';
import loader from 'assets/images/datahub-loader.svg';

import 'assets/scss/custom/pages/_rankings.scss';
import '../overview/ComplaintsOverview.scss';
import './ComplaintIntelligence.scss';

type FilterFormValues = {
  company: string;
  brand: string;
  windowRange: Date[] | null;
  compareRange: Date[] | null;
  customerClass: string;
};

type IntelligenceData = {
  filters: {
    brandId: string;
    brandName: string;
    windowMonths: number;
    compareMonths: number;
    customerClass: string;
  };
  summary: {
    complaints: number | null;
    complaintsNote: string;
    latestAvailableMonth?: string;
    currentStartMonth?: string;
    currentEndMonth?: string;
    priorStartMonth?: string;
    priorEndMonth?: string;
    statesWithIssues: number | null;
    statesWithIssuesNote: string;
    priorityType: string;
    priorityTypeNote: string;
    issueCluster: string;
    issueClusterNote: string;
  };
  heatmap: {
    states: string[];
    types: string[];
    cells: Array<{
      state: string;
      type: string;
      count: number | null;
      weightedCount?: number | null;
      priorityName?: string | null;
      priorityScore?: number;
      intensity: 'none' | 'low' | 'medium' | 'high';
    }>;
  };
  issueQueue: Array<{ text: string; kind: string; emphasis: string; weightedCount?: number }>;
  issueComparison: Array<{ label: string; deltaPct: number; color: string }>;
};

const getMonthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const getMonthRange = (monthCount: number, endDate = new Date()): [Date, Date] => {
  const end = getMonthStart(endDate);
  const start = new Date(end.getFullYear(), end.getMonth() - (monthCount - 1), 1);
  return [start, end];
};
const toMonthParam = (value: unknown) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-01`;
  }
  return null;
};
const formatMonthLabel = (value?: Date | null) => {
  if (!value) return '';
  return value.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const intelligenceClassOptions = [
  { id: 'All', name: 'All', value: 'All' },
  { id: 'Residential', name: 'Residential', value: 'Residential' },
  { id: 'Commercial', name: 'Commercial', value: 'Commercial' },
];
const ALL_VALUE = 'All';
const countDisplay = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

const renderTitleWithRange = (title: string) => {
  const parts = title.split(/(\s\([^)]+\))/g).filter(Boolean);
  if (parts.length <= 1) return <span>{title}</span>;

  return <span>{parts.map((part, index) => (/^\s\([^)]+\)$/.test(part) ? <span key={index} className="complaints-overview__panel-title-range">{part}</span> : <span key={index}>{part}</span>))}</span>;
};

const SummaryCardWithLoader = ({
  eyebrow,
  primary,
  secondary,
  tooltip,
  loading,
}: {
  eyebrow: string;
  primary: string;
  secondary?: string;
  tooltip: string;
  loading: boolean;
}) => (
  <div className="complaints-overview__summary-card h-100">
    <div className="complaints-overview__eyebrow complaints-overview__summary-eyebrow d-flex align-items-center gap-2">
      <span>{eyebrow}</span>
      <OverlayTrigger placement="top" overlay={<Tooltip id={`complaint-intelligence-summary-${eyebrow.replace(/\s+/g, '-').toLowerCase()}`}>{tooltip}</Tooltip>}>
        <i className="mdi mdi-information-outline" role="button" aria-label={`${eyebrow} details`} />
      </OverlayTrigger>
    </div>
    <div className="d-flex align-items-center gap-2 flex-wrap complaints-overview__summary-content">
      {loading ? (
        <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: 44 }}>
          <img src={loader} height={36} alt="Loading" />
        </div>
      ) : (
        <>
          <div className="complaints-overview__hero-number">{primary}</div>
          {secondary ? <div className="complaints-overview__hero-note">{secondary}</div> : null}
        </>
      )}
    </div>
  </div>
);

const FilterLoader = () => (
  <div className="mb-4 ranking-filters">
    <div className="row g-3 ranking-section align-items-end">
      {[0, 1, 2, 3].map((key) => (
        <div key={key} className="col-12 col-md-6 col-xl">
          <div className="complaints-overview__placeholder-line complaints-overview__placeholder-line--sm mb-2" />
          <div className="complaints-overview__placeholder-line complaints-overview__placeholder-line--md complaints-overview__filter-skeleton" />
        </div>
      ))}
      <div className="col-12 col-xl-auto complaints-overview__actions-col">
        <div className="d-flex justify-content-xl-end gap-2 complaints-overview__actions">
          <div className="complaints-overview__placeholder-line complaints-overview__button-skeleton" />
          <div className="complaints-overview__placeholder-line complaints-overview__button-skeleton" />
        </div>
      </div>
    </div>
  </div>
);

const HeatmapPanel = ({
  data,
  loading,
  windowLabel,
  rangeLabel,
}: {
  data: IntelligenceData['heatmap'];
  loading: boolean;
  windowLabel: string;
  rangeLabel?: string;
}) => {
  const colorMap = {
    none: '#f5f7fb',
    low: '#7ccba2',
    medium: '#f3c567',
    high: '#e57373',
  };

  return (
    <div className="complaints-overview__panel h-100">
      <div className="complaints-overview__panel-title d-flex align-items-center gap-2">
        <span>State x complaint type heatmap, {windowLabel}{rangeLabel ? ` (${rangeLabel})` : ''}</span>
        <OverlayTrigger placement="top" overlay={<Tooltip id="complaint-intelligence-heatmap-tooltip">Complaint distribution by state and mapped complaint type for the selected filters.</Tooltip>}>
          <i className="mdi mdi-information-outline" role="button" aria-label="Heatmap details" />
        </OverlayTrigger>
      </div>
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 220 }}>
          <img src={loader} height={44} alt="Loading" />
        </div>
      ) : data.states.length === 0 || data.types.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 220 }}>
          Heatmap data is not available for the selected filters.
        </div>
      ) : (
        <div className="complaint-intelligence__heatmap-grid" style={{ gridTemplateColumns: `56px repeat(${data.types.length}, minmax(0, 1fr))` }}>
          <div className="complaint-intelligence__heatmap-corner" />
          {data.types.map((type) => <div key={type} className="complaint-intelligence__heatmap-header">{type}</div>)}
          {data.states.map((state) => (
            <Fragment key={state}>
              <div key={`${state}-label`} className="complaint-intelligence__heatmap-state">{state}</div>
              {data.types.map((type) => {
                const cell = data.cells.find((item) => item.state === state && item.type === type);
                return (
                  <OverlayTrigger
                    key={`${state}-${type}`}
                    placement="top"
                      overlay={<Tooltip className="complaint-intelligence__tooltip" id={`heatmap-${state}-${type}`}>{`${state} ${type}: ${cell?.count === null || cell?.count === undefined ? '' : cell.count}`}</Tooltip>}
                  >
                    <div
                      className="complaint-intelligence__heatmap-cell"
                      style={{
                        backgroundColor: colorMap[cell?.intensity || 'none'],
                        color: (cell?.intensity || 'none') === 'none' ? '#bcc7d9' : '#1f2a44',
                      }}
                    >
                      {cell ? countDisplay(cell.count) : ''}
                    </div>
                  </OverlayTrigger>
                );
              })}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const QueuePanel = ({ title, items, loading }: { title: string; items: Array<{ text: string; kind: string; emphasis: string }>; loading: boolean }) => (
  <div className="complaints-overview__panel h-100">
    <div className="complaints-overview__panel-title d-flex align-items-center gap-2">
      {renderTitleWithRange(title)}
      <OverlayTrigger placement="top" overlay={<Tooltip id="complaint-intelligence-queue-tooltip">Priority queue of issues based on severity signals in the selected window.</Tooltip>}>
        <i className="mdi mdi-information-outline" role="button" aria-label="Issue queue details" />
      </OverlayTrigger>
    </div>
    {loading ? (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 180 }}>
        <img src={loader} height={44} alt="Loading" />
      </div>
    ) : items.length === 0 ? (
      <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 180 }}>
        No issue queue items for current selection.
      </div>
    ) : (
      <div className="complaints-overview__takeaway-list">
        {items.map((item) => (
          <div key={item.text} className="complaints-overview__takeaway-item">
            <p className="complaints-overview__takeaway-text">{item.text}</p>
            <div className="complaints-overview__takeaway-meta">
              <span className="badge badge-outline-secondary">{item.kind}</span>
              <span className="complaints-overview__takeaway-emphasis">{item.emphasis}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const IssueComparison = ({ title, data, loading }: { title: string; data: IntelligenceData['issueComparison']; loading: boolean }) => {
  const maxValue = Math.max(...data.map((item) => Math.abs(item.deltaPct)), 1);

  return (
    <div className="complaints-overview__panel h-100">
      <div className="complaints-overview__panel-title d-flex align-items-center gap-2">
        {renderTitleWithRange(title)}
        <OverlayTrigger placement="top" overlay={<Tooltip id="complaint-intelligence-comparison-tooltip">Percent change by complaint issue bucket versus the prior comparison window.</Tooltip>}>
          <i className="mdi mdi-information-outline" role="button" aria-label="Issue comparison details" />
        </OverlayTrigger>
      </div>
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 180 }}>
          <img src={loader} height={44} alt="Loading" />
        </div>
      ) : data.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 180 }}>
          Issue comparison data is not available for the selected filters.
        </div>
      ) : (
        <>
          <div className="complaints-overview__comparison-scale">
            <span className="complaints-overview__comparison-scale-spacer" />
            <span className="complaints-overview__comparison-scale-track">
              <span>-{maxValue.toFixed(1)}%</span>
              <span>0%</span>
              <span>+{maxValue.toFixed(1)}%</span>
            </span>
            <span className="complaints-overview__comparison-scale-value-spacer" />
          </div>
          <div className="complaints-overview__comparison-row">
            {data.map((item) => (
              <div key={item.label} className="complaints-overview__comparison-item">
                <div className="complaints-overview__comparison-label">{item.label}</div>
                <div className="complaints-overview__comparison-track complaints-overview__comparison-track--centered">
                  <div className="complaints-overview__comparison-zero" />
                  {item.deltaPct !== 0 ? (
                    <div
                      className={`complaints-overview__comparison-fill ${item.deltaPct < 0 ? 'complaints-overview__comparison-fill--negative' : 'complaints-overview__comparison-fill--positive'}`}
                      style={{ width: `${Math.max((Math.abs(item.deltaPct) / maxValue) * 50, 6)}%`, backgroundColor: item.color }}
                    />
                  ) : null}
                </div>
                <div className="complaints-overview__comparison-value">{item.deltaPct > 0 ? '+' : ''}{item.deltaPct}%</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ComplaintIntelligence = () => {
  const initialFilters = {
    company: '',
    brand: '',
    windowRange: getMonthRange(12),
    compareRange: getMonthRange(12, new Date(new Date().getFullYear(), new Date().getMonth() - 12, 1)),
    customerClass: intelligenceClassOptions[0].value,
  };

  const { register, handleSubmit, reset, setValue, watch, control } = useForm<FilterFormValues>({ defaultValues: initialFilters });
  const [defaultFilters, setDefaultFilters] = useState<FilterFormValues>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<FilterFormValues>(initialFilters);
  const [companyChoices, setCompanyChoices] = useState<Array<{ id: string; name: string; value: string }>>([]);
  const [brandChoices, setBrandChoices] = useState<Array<{ id: string; name: string; value: string }>>([]);
  const [companyBrandsMap, setCompanyBrandsMap] = useState<Record<string, Array<{ id: string; name: string; value: string }>>>({});
  const [filterOptionsSnapshot, setFilterOptionsSnapshot] = useState<{ customerClasses?: string[]; complaintTypes?: Array<{ complaintName?: string } | string>; brandStates?: Array<{ brandId: string; stateCode: string }> }>({});
  const [classChoices, setClassChoices] = useState(intelligenceClassOptions);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [hasIntelligenceData, setHasIntelligenceData] = useState(false);
  const showContentLoading = filtersLoading || dataLoading || !hasIntelligenceData;
  const [data, setData] = useState<IntelligenceData>({
    filters: { brandId: '', brandName: '', windowMonths: 12, compareMonths: 12, customerClass: 'All' },
    summary: {
      complaints: null,
      complaintsNote: 'context only',
      latestAvailableMonth: undefined,
      currentStartMonth: undefined,
      currentEndMonth: undefined,
      priorStartMonth: undefined,
      priorEndMonth: undefined,
      statesWithIssues: null,
      statesWithIssuesNote: 'active',
      priorityType: '-',
      priorityTypeNote: 'present',
      issueCluster: '-',
      issueClusterNote: 'persistent',
    },
    heatmap: { states: [], types: [], cells: [] },
    issueQueue: [],
    issueComparison: [],
  });

  const selectedFilterPills = useMemo(
    () => [
      ['Company', companyChoices.find((item) => item.value === appliedFilters.company)?.name || appliedFilters.company],
      ['Brand', brandChoices.find((item) => item.value === appliedFilters.brand)?.name || appliedFilters.brand],
      ['Window', Array.isArray(appliedFilters.windowRange) && appliedFilters.windowRange.length === 2 ? `${formatMonthLabel(appliedFilters.windowRange[0])}-${formatMonthLabel(appliedFilters.windowRange[1])}` : ''],
      ['Compare', Array.isArray(appliedFilters.compareRange) && appliedFilters.compareRange.length === 2 ? `${formatMonthLabel(appliedFilters.compareRange[0])}-${formatMonthLabel(appliedFilters.compareRange[1])}` : ''],
      ['Class', classChoices.find((item) => item.value === appliedFilters.customerClass)?.name || appliedFilters.customerClass],
    ],
    [appliedFilters, companyChoices, brandChoices, classChoices]
  );
  const selectedCompanyValue = watch('company');
  const selectedCompanyOption = useMemo(
    () => companyChoices.find((option) => option.value === selectedCompanyValue) || null,
    [companyChoices, selectedCompanyValue]
  );
  const selectedBrandValue = watch('brand');
  const selectedBrandOption = useMemo(
    () => brandChoices.find((option) => option.value === selectedBrandValue) || null,
    [brandChoices, selectedBrandValue]
  );
  const selectedClassValue = watch('customerClass');
  const selectedClassOption = useMemo(
    () => classChoices.find((option) => option.value === selectedClassValue) || null,
    [classChoices, selectedClassValue]
  );

  const activeWindowLabel = 'Window';
  const activeCompareLabel = 'Compare window';

  const formatMonth = (value?: string) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };

  const currentRangeLabel = useMemo(() => {
    const start = formatMonth(data.summary.currentStartMonth);
    const end = formatMonth(data.summary.currentEndMonth);
    return start !== '-' && end !== '-' ? `${start}-${end}` : '';
  }, [data.summary.currentStartMonth, data.summary.currentEndMonth]);

  const derivedCurrentRangeLabel = useMemo(() => {
    if (currentRangeLabel) return currentRangeLabel;
    const latest = data.summary.latestAvailableMonth;
    if (!latest) return '';

    const endDate = new Date(latest);
    if (Number.isNaN(endDate.getTime())) return '';

    const months = 12;
    const startDate = new Date(endDate);
    startDate.setMonth(startDate.getMonth() - (months - 1));

    const start = formatMonth(startDate.toISOString());
    const end = formatMonth(endDate.toISOString());
    return start !== '-' && end !== '-' ? `${start}-${end}` : '';
  }, [currentRangeLabel, data.summary.latestAvailableMonth]);

  const priorRangeLabel = useMemo(() => {
    const start = formatMonth(data.summary.priorStartMonth);
    const end = formatMonth(data.summary.priorEndMonth);
    return start !== '-' && end !== '-' ? `${start}-${end}` : '';
  }, [data.summary.priorStartMonth, data.summary.priorEndMonth]);

  useEffect(() => {
    document.title = 'DataHub - Complaint Intelligence';

    const loadFilters = async () => {
      try {
        const response = await getComplaintFilterOptions();
        setFilterOptionsSnapshot(response || {});
        const companies = (response?.companies || []).map((item: { companyId: string; companyName: string }) => ({ id: item.companyId, name: item.companyName, value: item.companyId }));
        const companyBrands = (response?.companyBrands || []) as Array<{ companyId: string; brandId: string; brandName: string }>;
        const nextCompanyBrandsMap = companyBrands.reduce((acc, row) => {
          if (!row?.companyId || !row?.brandId) return acc;
          if (!acc[row.companyId]) acc[row.companyId] = [];
          if (!acc[row.companyId].some((item) => item.value === row.brandId)) {
            acc[row.companyId].push({ id: row.brandId, name: row.brandName, value: row.brandId });
          }
          return acc;
        }, {} as Record<string, Array<{ id: string; name: string; value: string }>>);
        Object.values(nextCompanyBrandsMap).forEach((items) => items.sort((a, b) => a.name.localeCompare(b.name)));
        const defaultCompany = companies[0]?.value || '';
        const stored = loadSharedComplaintFilters();
        const selectedCompany = companies.some((item) => item.value === stored.company) ? (stored.company as string) : defaultCompany;
        const brands = nextCompanyBrandsMap[selectedCompany] || [];
        const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
        const selectedBrand = brandsWithAll.some((item) => item.value === stored.brand) ? (stored.brand as string) : ALL_VALUE;
        const classes = [{ id: 'All', name: 'All', value: 'All' }, ...(response?.customerClasses || []).filter((item: string) => item && item !== 'All').map((item: string) => ({ id: item, name: item, value: item }))];
        const resetDefaults = {
          company: selectedCompany,
          brand: ALL_VALUE,
          windowRange: getMonthRange(12),
          compareRange: getMonthRange(12, new Date(new Date().getFullYear(), new Date().getMonth() - 12, 1)),
          customerClass: 'All',
        };
        const nextDefaults = {
          ...resetDefaults,
          brand: selectedBrand,
          windowRange: stored.windowRange || resetDefaults.windowRange,
          compareRange: stored.compareRange || resetDefaults.compareRange,
          customerClass: classes.some((item) => item.value === stored.customerClass) ? (stored.customerClass as string) : 'All',
        };

        setCompanyChoices(companies);
        setCompanyBrandsMap(nextCompanyBrandsMap);
        setBrandChoices(brandsWithAll);
        setClassChoices(classes);
        setDefaultFilters(resetDefaults);
        reset(nextDefaults);
        setAppliedFilters(nextDefaults);
      } catch (error) {
        console.error('Failed to load complaints intelligence filters', error);
        setHasIntelligenceData(true);
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    if (!appliedFilters.company) {
      if (!filtersLoading) {
        setHasIntelligenceData(true);
      }
      setDataLoading(false);
      return;
    }

    setDataLoading(true);

    const loadIntelligence = async () => {
      try {
        const classes = [{ id: 'All', name: 'All', value: 'All' }, ...((filterOptionsSnapshot?.customerClasses || []) as string[]).filter((item: string) => item && item !== 'All').map((item: string) => ({ id: item, name: item, value: item }))];
        setClassChoices(classes);
        const filteredStates = ((filterOptionsSnapshot?.brandStates || []) as Array<{ brandId: string; stateCode: string }>)
          .filter((row) => appliedFilters.brand === ALL_VALUE || row?.brandId === appliedFilters.brand)
          .map((row) => row.stateCode)
          .filter((item, index, arr) => !!item && arr.indexOf(item) === index)
          .sort();
        const intelligenceResponse = await getComplaintIntelligence({
          complaintCompanyId: appliedFilters.company,
          brandId: appliedFilters.brand === ALL_VALUE ? null : appliedFilters.brand,
          brandName: appliedFilters.brand === ALL_VALUE ? 'All Brands' : (brandChoices.find((item) => item.value === appliedFilters.brand)?.name || ''),
          fromMonth: toMonthParam(Array.isArray(appliedFilters.windowRange) ? appliedFilters.windowRange[0] : null),
          toMonth: toMonthParam(Array.isArray(appliedFilters.windowRange) ? appliedFilters.windowRange[1] : null),
          compareFromMonth: toMonthParam(Array.isArray(appliedFilters.compareRange) ? appliedFilters.compareRange[0] : null),
          compareToMonth: toMonthParam(Array.isArray(appliedFilters.compareRange) ? appliedFilters.compareRange[1] : null),
          customerClass: appliedFilters.customerClass,
        }, { states: filteredStates, complaintTypes: filterOptionsSnapshot?.complaintTypes || [] });

        setData(intelligenceResponse);
        setHasIntelligenceData(true);
      } catch (error) {
        console.error('Failed to load complaints intelligence', error);
        setData((current) => ({
          ...current,
          summary: {
            ...current.summary,
            complaints: null,
            statesWithIssues: null,
            priorityType: '--',
            issueCluster: '--',
            priorityTypeNote: '',
            issueClusterNote: '',
          },
          heatmap: { states: [], types: [], cells: [] },
          issueQueue: [],
          issueComparison: [],
        }));
        setHasIntelligenceData(true);
      } finally {
        setDataLoading(false);
      }
    };

    loadIntelligence();
  }, [appliedFilters, filtersLoading]);

  const onDeleteFilter = (label: string) => {
    const fieldMap: Record<string, keyof FilterFormValues> = {
      Company: 'company',
      Brand: 'brand',
      Window: 'windowRange',
      Compare: 'compareRange',
      Class: 'customerClass',
    };
    const field = fieldMap[label];
    if (!field) return;
    setValue(field, defaultFilters[field]);
    const next = { ...appliedFilters, [field]: defaultFilters[field] };
    setAppliedFilters(next);
    saveSharedComplaintFilters(next);
  };

  return (
    <div className="complaints-overview complaint-intelligence">
      <Div>
        <PageTitle title="Complaint Intelligence" />
      </Div>

      <Card className="ranking-module complaints-overview__module border-0">
        <Card.Body>
          {filtersLoading ? (
            <FilterLoader />
          ) : (
            <Form onSubmit={handleSubmit(async (values) => {
              const classes = [{ id: 'All', name: 'All', value: 'All' }, ...((filterOptionsSnapshot?.customerClasses || []) as string[]).filter((item: string) => item && item !== 'All').map((item: string) => ({ id: item, name: item, value: item }))];
              const brands = companyBrandsMap[values.company] || [];
              const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
              const nextValues = {
                ...values,
                brand: brandsWithAll.some((item) => item.value === values.brand) ? values.brand : ALL_VALUE,
                customerClass: classes.some((item) => item.value === values.customerClass) ? values.customerClass : 'All',
              };
              setBrandChoices(brandsWithAll);
              setClassChoices(classes);
              reset(nextValues);
              setAppliedFilters(nextValues);
              saveSharedComplaintFilters(nextValues);
            })} className="ranking-filters mb-4">
              <Div className="mb-2 ranking-section">
                <Row className="g-3 align-items-end">
                  <Col xl md={6}>
                    <Form.Group className="complaints-overview__select">
                      <Form.Label className="form-label geography-label">Company</Form.Label>
                      <Select
                        className="react-select"
                        classNamePrefix="react-select"
                        options={companyChoices.map((option) => ({ value: option.value, label: option.name }))}
                        value={selectedCompanyOption ? { value: selectedCompanyOption.value, label: selectedCompanyOption.name } : null}
                        onChange={(option: { value: string; label: string } | null) => {
                          const companyValue = option?.value || '';
                          const brands = companyBrandsMap[companyValue] || [];
                          const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
                          setValue('company', companyValue);
                          setValue('brand', ALL_VALUE);
                          setBrandChoices(brandsWithAll);
                        }}
                        isSearchable
                        isClearable={false}
                      />
                      <input type="hidden" {...register('company')} />
                    </Form.Group>
                  </Col>
                  <Col xl md={6}><Form.Group className="complaints-overview__select"><Form.Label className="form-label geography-label">Brand</Form.Label><Select className="react-select" classNamePrefix="react-select" options={brandChoices.map((option) => ({ value: option.value, label: option.name }))} value={selectedBrandOption ? { value: selectedBrandOption.value, label: selectedBrandOption.name } : null} onChange={(option: { value: string; label: string } | null) => { setValue('brand', option?.value || ''); }} isSearchable isClearable={false} /><input type="hidden" {...register('brand')} /></Form.Group></Col>
                  <Col xl md={6}><FormInput type="date" label="Window" name="windowRange" control={control} errors={{}} range onlyMonthPicker readOnly labelClassName="form-label geography-label" placeholder="mm/yyyy - mm/yyyy" validation={{ validate: (value: unknown) => Array.isArray(value) && value.length === 2 ? true : 'Window From and To are required' }} /></Col>
                  <Col xl md={6}><FormInput type="date" label="Compare" name="compareRange" control={control} errors={{}} range onlyMonthPicker readOnly labelClassName="form-label geography-label" placeholder="mm/yyyy - mm/yyyy" validation={{ validate: (value: unknown) => Array.isArray(value) && value.length === 2 ? true : 'Compare From and To are required' }} /></Col>
                  <Col xl md={6}>
                    <Form.Group className="complaints-overview__select">
                      <Form.Label className="form-label geography-label">Class</Form.Label>
                      <Select
                        className="react-select"
                        classNamePrefix="react-select"
                        options={classChoices.map((option) => ({ value: option.value, label: option.name }))}
                        value={selectedClassOption ? { value: selectedClassOption.value, label: selectedClassOption.name } : null}
                        onChange={(option: { value: string; label: string } | null) => setValue('customerClass', option?.value || '')}
                        isSearchable
                        isClearable={false}
                      />
                      <input type="hidden" {...register('customerClass')} />
                    </Form.Group>
                  </Col>
                  <Col xl="auto" xs={12} className="complaints-overview__actions-col"><Div className="d-flex justify-content-xl-end gap-2 complaints-overview__actions"><Button type="button" variant="primary" onClick={() => { reset(defaultFilters); setAppliedFilters(defaultFilters); saveSharedComplaintFilters(defaultFilters); }}>Reset</Button><Button type="submit" variant="primary">Apply</Button></Div></Col>
                </Row>
              </Div>
              <Div className="mb-2 ranking-section"><Div className="d-flex flex-wrap gap-2 complaints-overview__pill-row">{selectedFilterPills.map(([label, value]) => <Badge key={label} className="mx-1" bg="secondary">{label}: {value}<i className="mdi mdi-close" onClick={() => onDeleteFilter(label)} style={{ cursor: 'pointer', marginLeft: '5px' }} role="button" aria-label={`Remove ${label}`}></i></Badge>)}</Div></Div>
            </Form>
          )}

          <Row className="g-3 mb-3">
            <Col xl={3} md={6}><SummaryCardWithLoader eyebrow={`${activeWindowLabel} Complaints`} primary={countDisplay(data.summary.complaints)} secondary={data.summary.complaintsNote} tooltip="Total complaints across all mapped issue types for the selected filters." loading={showContentLoading} /></Col>
            <Col xl={3} md={6}><SummaryCardWithLoader eyebrow="States with issues" primary={countDisplay(data.summary.statesWithIssues)} secondary={data.summary.statesWithIssuesNote} tooltip="Count of states with active complaint volume in the selected window." loading={showContentLoading} /></Col>
            <Col xl={3} md={6}><SummaryCardWithLoader eyebrow="Highest-priority type active" primary={data.summary.priorityType} secondary={data.summary.priorityTypeNote} tooltip="Mapped complaint type with the strongest current priority signal." loading={showContentLoading} /></Col>
            <Col xl={3} md={6}><SummaryCardWithLoader eyebrow="Top issue cluster" primary={data.summary.issueCluster} secondary={data.summary.issueClusterNote} tooltip="State and complaint type combination with the strongest current issue concentration." loading={showContentLoading} /></Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col xl={12}><HeatmapPanel data={data.heatmap} loading={showContentLoading} windowLabel={activeWindowLabel} rangeLabel={derivedCurrentRangeLabel} /></Col>
          </Row>

          <Row className="g-3">
            <Col xl={6}><QueuePanel title="Issue queue" items={data.issueQueue} loading={showContentLoading} /></Col>
            <Col xl={6}><IssueComparison title={`${activeWindowLabel}${currentRangeLabel ? ` (${currentRangeLabel})` : ''} vs ${activeCompareLabel}${priorRangeLabel ? ` (${priorRangeLabel})` : ''} by issue bucket`} data={data.issueComparison} loading={showContentLoading} /></Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ComplaintIntelligence;
